export const navigation = {
  items: [
    { label: 'Research', href: '/research' },
    { label: 'Framework', href: '/framework' },
    { label: 'Institute', href: '/about' },
    { label: 'Contact', href: '/contact' },
  ],
};

export const hero = {
  headline: 'The deepest seam of human intelligence, now computationally accessible',
  description: 'For the first time, we can encode what the best experts were right about, not just what they could explain. This is the knowledge that has shaped every high-stakes decision in history, and it has never been accessible to machines. Until now.',
  cta: 'Read the Paper',
};

export const homeSections = {
  why: {
    number: 'One',
    label: 'The Third Seam',
    link: { text: 'More about Mithara', href: '/about' },
    heading: 'Current AI hits an invisible ceiling. We found the way through.',
    cols: [
      'Every AI system today can only learn what humans can teach: what we can label, what we can demonstrate, what we can put into words. But the best judgment, the kind that saves lives, prevents disasters, spots the invisible, operates beneath language. It reveals itself only in outcomes, years later.',
      'We built the first methodology to recover this knowledge. Not from what experts say. From what they were right about. This is the third seam of human intelligence. And we are the only institute in the world dedicated to making it computationally accessible.',
    ],
  },
  areas: {
    number: 'Two',
    label: 'Research',
    link: { text: 'All Research', href: '/research' },
    heading: 'Four breakthrough domains where expert judgment vastly exceeds current AI',
    description: 'Wherever senior judgment makes the difference between success and catastrophe, we can now encode what was previously uncodable.',
    cards: [
      {
        label: 'Discovery', value: 'Category III',
        desc: 'We identified and mathematically grounded the third category of knowledge: judgment that operates beneath language and reveals itself only through verified outcomes.',
        title: 'Sublinguistic Intelligence', href: '/research',
      },
      {
        label: 'Method', value: 'OGEE',
        desc: 'Our framework recovers expert judgment from long-run outcomes without requiring experts to explain themselves. If they were right, we can encode it.',
        title: 'Outcome-Grounded Encoding', href: '/framework',
      },
      {
        label: 'Proof', value: 'Live system',
        desc: 'AI Compliance Intelligence Layer predicts regulatory clearance across 60+ jurisdictions before submission. Built on what compliance officers were right about, not what they could articulate.',
        title: "Beyond the Labeller's Ceiling", href: '/framework#aicil',
      },
      {
        label: 'Foundation', value: 'Pragmatist',
        desc: 'Truth is what reliably produces good outcomes. This philosophical grounding lets us encode judgment from verified consequences, even when the expert cannot explain why.',
        title: 'Epistemic Theory', href: '/about',
      },
    ],
  },
  impact: {
    number: 'Three',
    label: 'Why This Matters',
    heading: 'The ceiling holding AI back is invisible. But it is absolute.',
    cols: [
      'Current AI can only learn from what humans can label or demonstrate. That means it caps out at human teaching ability, not human capability. The expert surgeon who saves the difficult case, the compliance officer who spots the invisible risk: their judgment fires before words. It lives in pattern recognition that took decades to build.',
      'We are the first to encode this. Not by asking experts to explain. By learning from what they were right about. This is not incremental. This opens the third seam of intelligence that has shaped expert decisions for millennia but has never been computationally accessible.',
    ],
    stats: [
      { value: '3', label: 'Categories of knowledge' },
      { value: '1st', label: 'Method to encode the third' },
    ],
  },
  applications: {
    number: 'Four',
    label: 'Where This Changes Everything',
    link: { text: 'See Applications', href: '/framework#aicil' },
    heading: 'Wherever stakes are high and judgment matters most',
    description: 'These are the domains where AI has hit the ceiling. Where senior expertise vastly exceeds what can be labeled or demonstrated. Where decisions determine outcomes years later.',
    items: [
      {
        name: 'Cross-border\ncompliance',
        desc: 'Predict regulatory clearance before submission. Not from rules. From what senior compliance officers were right about across 60+ jurisdictions.',
        meta: { label: 'Market', value: '$200T global trade' },
      },
      {
        name: 'Clinical\ndiagnosis',
        desc: 'Encode the pattern recognition of experienced clinicians. The judgment that fires before reasoning begins. The difference between correct diagnosis and catastrophe.',
        meta: { label: 'Ceiling', value: 'Beyond demonstration' },
      },
      {
        name: 'Financial\nrisk',
        desc: 'Recover the implicit judgment of expert risk assessment from verified portfolio outcomes. Not from what they said. From what they were right about.',
        meta: { label: 'Advantage', value: 'Long-run outcomes' },
      },
      {
        name: 'Defense\nISR',
        desc: 'Intelligence and surveillance decisions under uncertainty. Where expert judgment operates beneath conscious articulation and only reveals itself in mission success.',
        meta: { label: 'Stakes', value: 'National security' },
      },
    ],
  },
};

export const researchContent = {
  heroTitle: 'Sublinguistic Intelligence',
  heroDesc: 'A third epistemic category and its computational encoding. Formally defined, mathematically grounded, empirically validated.',
  ceiling: {
    heading: 'The Labeller\'s Ceiling',
    desc: 'Contemporary AI systems share a structural constraint: their performance cannot exceed that of the humans who generated their training signal. This reflects a fundamental partition in the nature of human knowledge itself.',
    insight: 'No system trained from articulated or demonstrated knowledge can exceed the outcome quality of the best labeller, regardless of model capacity, training compute, or architectural choice.',
  },
  categories: [
    {
      title: 'Explicit Knowledge', highlighted: false,
      desc: 'Propositionally representable and communicable through language without decision-relevant loss.',
      props: [
        { label: 'Articulable', value: 'Yes' },
        { label: 'Demonstrable', value: 'Yes' },
        { label: 'Training', value: 'Labels & rules' },
        { label: 'Ceiling', value: 'Best labeller' },
      ],
    },
    {
      title: 'Tacit Knowledge', highlighted: false,
      desc: 'Resists complete articulation but admits transmission through embodied demonstration.',
      props: [
        { label: 'Articulable', value: 'Partial' },
        { label: 'Demonstrable', value: 'Yes' },
        { label: 'Training', value: 'Demonstrations' },
        { label: 'Ceiling', value: 'Best demonstrator' },
      ],
    },
    {
      title: 'Sublinguistic Intelligence', highlighted: true,
      desc: 'Neither articulable nor demonstrable. Recoverable exclusively through verified long-run outcomes.',
      props: [
        { label: 'Articulable', value: 'No' },
        { label: 'Demonstrable', value: 'No' },
        { label: 'Training', value: 'Verified outcomes' },
        { label: 'Ceiling', value: 'Bayes-optimal' },
      ],
    },
  ],
  keyInsight: 'In any domain where the best practitioners possess Category III knowledge, the labeller\'s ceiling lies strictly below expert-level performance. No investment in scale can close this gap within existing paradigms.',
};

export const frameworkContent = {
  heroTitle: 'Outcome-Grounded Expert Encoding',
  heroDesc: 'The first computational methodology capable of encoding sublinguistic intelligence: learning from what experts were right about.',
  overview: 'OGEE addresses the labeller\'s ceiling by substituting a fundamentally different training signal. Rather than learning from what experts articulate or demonstrate, it learns from what experts were right about.',
  steps: [
    { number: '01', title: 'Observe', desc: 'Expert practitioners observed in naturalistic workflows without elicitation or interruption. We do not ask them to explain.' },
    { number: '02', title: 'Match', desc: 'Each decision paired with its verified long-run outcome: what actually resulted, not what was intended.' },
    { number: '03', title: 'Recover', desc: 'Maximum entropy inverse reinforcement learning identifies the implicit reward structure of expert decisions.' },
    { number: '04', title: 'Deploy', desc: 'Systems that predict with Bayes-optimal accuracy, exceeding any label-trained or demonstration-trained baseline.' },
  ],
  theorem: 'As the dataset grows, OGEE-trained systems converge to the Bayes-optimal classifier, bounded only by the irreducible uncertainty in the outcome distribution itself.',
  aicil: {
    title: 'AICIL: AI Compliance Intelligence Layer',
    subtitle: 'Eliminates the $40 trillion cross-border payment uncertainty problem',
    oneSentence: 'AICIL predicts whether a cross-border payment will clear before you send it, trained on what compliance experts were right about, not what they could explain.',
    
    problem: {
      heading: 'The invisible ceiling in cross-border payments',
      body: 'Twenty percent of all international wire transfers freeze mid-flight. The payment stops. An investigation begins. Documentation gets scrambled. Three to twenty-one days pass. This happens to $40 trillion in transactions every year. The root cause is not bad actors. It is that compliance systems were built on rules, rules that cannot capture what experienced compliance officers actually know. The best judgment in the world operates beneath those rules. AICIL encodes it.',
    },
    
    how: {
      heading: 'How it works',
      stages: [
        {
          number: '01',
          title: 'Before the wire',
          desc: 'Submit payment details before initiating transfer. AICIL receives origin, destination, amount, currency, counterparties, business purpose.',
        },
        {
          number: '02',
          title: 'Screening',
          desc: 'Maps the complete correspondent banking chain. Screens 32,453 global banks, 19,703 sanctions entries, 8 major payment systems. Not with rules, with the encoded judgment of expert compliance officers.',
        },
        {
          number: '03',
          title: 'Clearance prediction',
          desc: 'Returns prediction in seconds. If it will clear: generates jurisdiction-specific compliance dossier. If it will not: identifies what is missing, generates required documentation before submission.',
        },
      ],
    },
    
    different: {
      heading: 'What makes it different',
      body: 'Every other compliance system learns from what experts could articulate. That creates a ceiling at 92 to 95 percent accuracy. AICIL uses OGEE to learn from what experts were right about. It observes naturalistic workflows, pairs decisions with verified outcomes, recovers the implicit judgment. The accuracy ceiling is Bayes-optimal, not labeller-bounded.',
    },
    
    production: {
      heading: 'Live production system',
      items: [
        '32,453 global banks',
        '19,703 sanctions entries',
        '8 payment systems (CHIPS, Fedwire, T2, EURO1, CHAPS, BOJ-NET, LVTS, UAEFTS)',
        'Complete authentication backend and dashboard',
      ],
    },
    
    stats: [
      { value: '$40T', label: 'Annual friction eliminated' },
      { value: '20%', label: 'Transactions currently delayed' },
      { value: '3–21', label: 'Days saved per incident' },
    ],
  },
  dataMoat: 'OGEE requires outcome-matched expert decision data, a resource that does not exist in any available dataset and cannot be synthesised. This creates a compounding structural advantage.',
};

export const aboutContent = {
  heroTitle: 'Mithara Institute Inc.',
  heroDesc: 'We study what cannot be spoken, yet determines everything.',
  philosophy: [
    'Mithara Institute Inc. was founded on a singular conviction: that the most consequential form of human intelligence has never been computationally accessible. Not because the technology was insufficient, but because the epistemological framework was incomplete.',
    'We draw on the pragmatist tradition, where truth is identified not with correspondence to description but with what reliably produces good outcomes.',
    'Our work bridges philosophy, cognitive science, and machine learning. Not merely to advance AI, but to access the deepest seam of human expertise that has never been reached.',
  ],
  expertise: [
    { title: 'Sublinguistic intelligence as a formal epistemic category', desc: 'Mathematical grounding of knowledge that operates entirely beneath language, distinct from both explicit and tacit knowledge.' },
    { title: 'Outcome-Grounded Expert Encoding (OGEE)', desc: 'The first computational methodology capable of recovering Category III knowledge from verified long-run outcomes.' },
    { title: 'Cross-border compliance intelligence (AICIL)', desc: 'Predicting clearance probability across 60+ regulatory jurisdictions before payment submission.' },
    { title: 'Epistemic theory and knowledge representation', desc: 'Formal foundations drawing on pragmatist philosophy and decision theory.' },
    { title: 'Inverse reinforcement learning for expert behaviour', desc: 'Maximum entropy IRL to identify implicit reward structures from naturalistic expert workflows.' },
  ],
  founder: {
    name: 'Aria Mohajer',
    role: 'Founder & Principal Researcher',
    bio: 'Leading the formal development of sublinguistic intelligence as a third epistemic category and OGEE as its computational encoding methodology.',
  },
  values: [
    { title: 'Epistemic Rigour', desc: 'Every claim formally grounded and empirically validated.' },
    { title: 'Human-Centred', desc: 'We study expertise to honour and amplify it, not replace it.' },
    { title: 'Outcome-Driven', desc: 'We measure truth by what works across verified consequences.' },
  ],
};

export const contactContent = {
  heroTitle: 'Connect',
  heroDesc: 'Questions, ideas, or collaborations: we are here.',
  description: 'Whether you are a researcher exploring the boundaries of knowledge representation, an institution seeking to encode deep expertise, or simply compelled by what lies beneath language, we would like to hear from you.',
  email: 'info@mithara.co',
  location: 'Menlo Park, San Francisco Bay Area',
  address: '101 Jefferson Drive, 1st Floor\nMenlo Park, California 94025\nUnited States of America',
  fields: [
    { name: 'name', label: 'Name', type: 'text', placeholder: 'Your name' },
    { name: 'email', label: 'Email', type: 'email', placeholder: 'your@email.com' },
    { name: 'subject', label: 'Subject', type: 'text', placeholder: 'What brings you here' },
    { name: 'message', label: 'Message', type: 'textarea', placeholder: 'Tell us more...' },
  ],
};

export const footer = {
  brand: 'Mithara',
  sections: [
    { num: '1.0', label: 'Pages', links: [
      { label: 'Research', href: '/research' },
      { label: 'Framework', href: '/framework' },
      { label: 'Institute', href: '/about' },
      { label: 'Contact', href: '/contact' },
    ]},
    { num: '2.0', label: 'Follow', links: [
      { label: 'LinkedIn', href: 'https://linkedin.com/company/mithara-institute' },
      { label: 'X', href: '#' },
    ]},
  ],
  legal: [
    { label: 'Privacy Policy', href: '#' },
    { label: 'Terms of Use', href: '#' },
  ],
  location: 'Menlo Park, CA',
};
